import {
  type AttachedFile,
  Button,
  DatePicker,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  FileAttach,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  TOUCH,
  useDialogDirty,
} from "@nymbl/nos2-rules";
import { Plus, Trash2 } from "lucide-react";
import { useId, useState } from "react";
import { toast } from "sonner";
import { PoPicker } from "@/components/expenses/PoPicker";
import {
  fileToBase64,
  type SaveClaimOutcome,
  useExpenseOptions,
  useSaveClaim,
  useScanReceipt,
} from "@/hooks/use-expenses";
import {
  type DraftLine,
  fileKey,
  lineBlank,
  mergeScanLines,
  newLine,
} from "@/lib/expenses/draft-lines";
import { offeredPos } from "@/lib/expenses/po";
import { RECEIPT_ACCEPT, receiptRefusal } from "@/lib/expenses/receipts";
import { isPoType } from "@/lib/expenses/rules";
import { EXPENSE_DESCRIPTION_MAX, EXPENSE_LINE_MAX, type ExpenseDetail } from "@/schemas/expenses";

interface ClaimDialogProps {
  /** A draft or rejected claim to edit; null for a new one. */
  editing: ExpenseDetail | null;
  onClose: () => void;
  /** After a partial save the claim is saved but unfinished, so it opens. */
  onSaved: (out: SaveClaimOutcome) => void;
}

/** What the lines hold, to tell whether they changed: their ids and keys are not an edit. */
const linesSnapshot = (lines: DraftLine[]) =>
  JSON.stringify(
    lines.map((l) => [
      l.date,
      l.amount,
      l.description,
      l.files.map(fileKey),
      l.keptReceipts.map((r) => r.id),
    ]),
  );

function linesOf(claim: ExpenseDetail | null): DraftLine[] {
  if (!claim || claim.lines.length === 0) return [newLine()];
  return claim.lines.map((l) => ({
    key: l.id,
    id: l.id,
    date: l.date ?? "",
    amount: String(l.amount),
    description: l.description ?? "",
    files: [],
    keptReceipts: claim.receipts
      .filter((r) => r.lineId === l.id)
      .map((r) => ({ id: r.id, fileName: r.fileName })),
  }));
}

const lineReady = (l: DraftLine) =>
  l.date !== "" && Number(l.amount) > 0 && l.description.trim() !== "";
function LineEditor({
  line,
  index,
  canRemove,
  showMissing,
  onChange,
  onRemove,
}: {
  line: DraftLine;
  index: number;
  canRemove: boolean;
  showMissing: boolean;
  onChange: (next: DraftLine) => void;
  onRemove: () => void;
}) {
  const n = index + 1;
  const attached = [
    ...line.keptReceipts.map((r) => ({ id: r.id, name: r.fileName })),
    ...line.files.map((f) => ({ id: fileKey(f), name: f.name, size: f.size })),
  ];

  function remove(file: AttachedFile) {
    onChange({
      ...line,
      keptReceipts: line.keptReceipts.filter((k) => k.id !== file.id),
      files: line.files.filter((f) => fileKey(f) !== file.id),
    });
  }

  async function pick(files: File[]) {
    const accepted: File[] = [];
    for (const f of files) {
      const refusal = receiptRefusal(f.name, new Uint8Array(await f.arrayBuffer()));
      if (refusal) toast.error(`${f.name}: ${refusal}`);
      else accepted.push(f);
    }
    const held = new Set(line.files.map(fileKey));
    const fresh = accepted.filter((f) => !held.has(fileKey(f)));
    onChange({ ...line, files: [...line.files, ...fresh] });
  }

  return (
    <fieldset className="flex flex-col gap-2 rounded-lg border p-3">
      <legend className="px-1 text-sm font-semibold">Line {n}</legend>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_8rem]">
        <DatePicker
          className={TOUCH}
          aria-invalid={showMissing && line.date === ""}
          value={line.date}
          onChange={(date) => onChange({ ...line, date })}
          aria-label={`Line ${n} date`}
        />
        <Input
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0.01"
          placeholder="Amount"
          className={TOUCH}
          aria-label={`Line ${n} amount`}
          aria-invalid={showMissing && !(Number(line.amount) > 0)}
          value={line.amount}
          onChange={(e) => onChange({ ...line, amount: e.target.value })}
        />
      </div>
      <Input
        placeholder="Description"
        className={TOUCH}
        maxLength={EXPENSE_LINE_MAX}
        aria-label={`Line ${n} description`}
        aria-invalid={showMissing && line.description.trim() === ""}
        value={line.description}
        onChange={(e) => onChange({ ...line, description: e.target.value })}
      />
      <FileAttach
        label={`Line ${n} receipts`}
        files={attached}
        accept={RECEIPT_ACCEPT}
        multiple
        onAdd={(files) => void pick(files)}
        onRemove={remove}
      />
      <div className="flex items-center justify-end gap-2">
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={TOUCH}
            aria-label={`Remove line ${n}`}
            onClick={onRemove}
          >
            <Trash2 className="size-4" aria-hidden="true" />
          </Button>
        )}
      </div>
    </fieldset>
  );
}

function ClaimForm({ editing, onClose, onSaved }: ClaimDialogProps) {
  const typeId = useId();
  const monthId = useId();
  const descId = useId();
  const options = useExpenseOptions(true);
  const save = useSaveClaim();
  const scan = useScanReceipt();
  const [type, setType] = useState(editing?.typeId ?? "");
  const [month, setMonth] = useState(editing?.periodId ?? "");
  const [poId, setPoId] = useState(editing?.poId ?? "");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [showMissing, setShowMissing] = useState(false);
  const [lines, setLines] = useState<DraftLine[]>(() => linesOf(editing));
  const [openedLines] = useState(() => linesSnapshot(lines));
  useDialogDirty(
    type !== (editing?.typeId ?? "") ||
      month !== (editing?.periodId ?? "") ||
      poId !== (editing?.poId ?? "") ||
      description !== (editing?.description ?? "") ||
      linesSnapshot(lines) !== openedLines,
  );

  if (options.isPending) {
    return (
      <DialogBody>
        <Skeleton className="h-72 w-full" />
      </DialogBody>
    );
  }
  if (options.isError) {
    return (
      <DialogBody>
        <ErrorState message="The form didn't load" onRetry={() => void options.refetch()} />
      </DialogBody>
    );
  }

  // An old Department POs claim keeps its type; it is shown but offered to nobody else (R2).
  const types = [...options.data.types];
  if (editing && !types.some((t) => t.id === editing.typeId)) {
    types.push({ id: editing.typeId, name: editing.typeName, typeEnum: editing.typeEnum });
  }
  // A month closed since the draft was made still shows; saving it says why it is refused.
  const months = [...options.data.months];
  if (editing && !months.some((m) => m.id === editing.periodId)) {
    months.push({ id: editing.periodId, name: editing.monthName, yearId: null });
  }
  const monthValue = month || options.data.defaultMonthId || "";
  const onPo = isPoType(types.find((t) => t.id === type)?.typeEnum ?? null);
  const yearId = months.find((m) => m.id === monthValue)?.yearId ?? null;
  // A claim's own PO stays on offer only while its year does (V3).
  const startYearId = months.find((m) => m.id === editing?.periodId)?.yearId ?? null;
  const sameYear =
    editing !== null &&
    (monthValue === editing.periodId || (yearId !== null && yearId === startYearId));
  const pos = offeredPos(options.data.pos, yearId, sameYear ? (editing?.poId ?? null) : null);
  const poValue = pos.some((p) => p.id === poId) ? poId : "";
  const filled = lines.filter((l) => !lineBlank(l));
  const headerReady =
    type !== "" && monthValue !== "" && description.trim() !== "" && (!onPo || poValue !== "");
  const linesReady = filled.every(lineReady);
  const canSave = headerReady && linesReady;
  const canSubmit = canSave && filled.length > 0;

  /** Sends when the form is complete; otherwise marks what is missing. */
  function attempt(submit: boolean) {
    if (submit ? canSubmit : canSave) void send(submit);
    else setShowMissing(true);
  }

  /** Fills only what is still empty, from the state at the moment the answer lands. */
  async function scanFiles(picked: File[]) {
    if (!picked) return;
    const held = new Set(lines.flatMap((l) => l.files.map(fileKey)));
    const files: File[] = [];
    for (const f of Array.from(picked)) {
      const refusal = receiptRefusal(f.name, new Uint8Array(await f.arrayBuffer()));
      if (refusal) toast.error(`${f.name}: ${refusal}`);
      else if (!held.has(fileKey(f))) files.push(f);
    }
    if (files.length === 0) return;
    const sent = await Promise.all(
      files.map(async (f) => ({ fileName: f.name, data: await fileToBase64(f) })),
    );
    scan.mutate(
      { files: sent },
      {
        onSuccess: (found) => {
          if (found.typeId) setType((t) => t || (found.typeId ?? ""));
          if (found.description) setDescription((d) => d.trim() || (found.description ?? ""));
          if (found.monthId) setMonth((m) => m || (found.monthId ?? ""));
          setLines((current) => mergeScanLines(current, found, files));
          if (found.lines.length > 0) toast.success("Check the details before saving");
          else toast.error("Nothing could be read. Fill it in by hand.");
        },
      },
    );
  }

  async function send(submit: boolean) {
    const uploads: Array<{ lineIndex: number; fileName: string; data: string }> = [];
    for (const [lineIndex, line] of filled.entries()) {
      for (const f of line.files) {
        uploads.push({ lineIndex, fileName: f.name, data: await fileToBase64(f) });
      }
    }
    const kept = new Set(filled.flatMap((l) => l.keptReceipts.map((r) => r.id)));
    const lineKept = new Set(filled.flatMap((l) => (l.id ? [l.id] : [])));
    save.mutate(
      {
        claim: {
          ...(editing ? { id: editing.id } : {}),
          ...(onPo ? { poId: poValue } : {}),
          typeId: type,
          periodId: monthValue,
          description: description.trim(),
          lines: filled.map((l) => ({
            ...(l.id ? { id: l.id } : {}),
            date: l.date,
            amount: Number(l.amount),
            description: l.description.trim(),
          })),
        },
        uploads,
        // A removed line takes its receipts with it on the server.
        removeReceiptIds: (editing?.receipts ?? [])
          .filter((r) => lineKept.has(r.lineId) && !kept.has(r.id))
          .map((r) => r.id),
        submit,
      },
      { onSuccess: onSaved },
    );
  }

  return (
    <form
      className="flex min-h-0 flex-1 flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        attempt(true);
      }}
    >
      <DialogBody className="flex flex-col gap-4">
        {!editing && options.data.scanEnabled && (
          <FileAttach
            label="Scan receipt"
            files={[]}
            accept={RECEIPT_ACCEPT}
            multiple
            disabled={scan.isPending}
            hint={scan.isPending ? "Reading receipt" : undefined}
            onAdd={(files) => void scanFiles(files)}
            onRemove={() => undefined}
          />
        )}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor={typeId}>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger
                id={typeId}
                className={`${TOUCH} w-full`}
                aria-invalid={showMissing && type === ""}
              >
                <SelectValue placeholder="Pick a type" />
              </SelectTrigger>
              <SelectContent>
                {types.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor={monthId}>Month</Label>
            <Select value={monthValue} onValueChange={setMonth}>
              <SelectTrigger id={monthId} className={`${TOUCH} w-full`}>
                <SelectValue placeholder="Pick a month" />
              </SelectTrigger>
              <SelectContent>
                {months.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {onPo && <PoPicker pos={pos} value={poValue} invalid={showMissing} onChange={setPoId} />}
        <div className="flex flex-col gap-2">
          <Label htmlFor={descId}>Description</Label>
          <Input
            id={descId}
            className={TOUCH}
            aria-invalid={showMissing && description.trim() === ""}
            maxLength={EXPENSE_DESCRIPTION_MAX}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-3">
          {lines.map((line, i) => (
            <LineEditor
              key={line.key}
              line={line}
              index={i}
              canRemove={lines.length > 1}
              showMissing={showMissing && (!lineBlank(line) || filled.length === 0)}
              onChange={(next) => setLines(lines.map((l) => (l.key === line.key ? next : l)))}
              onRemove={() => setLines(lines.filter((l) => l.key !== line.key))}
            />
          ))}
          <Button
            type="button"
            variant="outline"
            className={`${TOUCH} self-start`}
            onClick={() => setLines([...lines, newLine()])}
          >
            <Plus className="size-4" aria-hidden="true" />
            Add line
          </Button>
        </div>
      </DialogBody>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          className={TOUCH}
          disabled={save.isPending}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="outline"
          className={TOUCH}
          disabled={save.isPending || scan.isPending}
          onClick={() => attempt(false)}
        >
          Save draft
        </Button>
        <Button type="submit" className={TOUCH} disabled={save.isPending || scan.isPending}>
          {editing?.approval === "rejected" ? "Resubmit" : "Submit"}
        </Button>
      </DialogFooter>
    </form>
  );
}

/** New expense, or edit a draft or rejected one. */
export function ClaimDialog(props: ClaimDialogProps) {
  const title = props.editing ? "Edit expense" : "New expense";
  const description = "Pick the type and month, add lines and receipts, then save or submit.";
  return (
    <Dialog open onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">{description}</DialogDescription>
        </DialogHeader>
        <ClaimForm {...props} />
      </DialogContent>
    </Dialog>
  );
}
